import type {
  ChoosePartyScreenState,
  PortalWording,
} from "@shared-core/domain/portal-sign-in/types";
import { Button } from "../primitives/button";
import { Card } from "../primitives/card";
import { cn } from "../primitives/utils";
import { SignInFrame } from "./SignInFrame";

export interface ChoosePartyScreenProps {
  state: ChoosePartyScreenState;
  wording: PortalWording;
  busy?: boolean;
  onChoose(partyId: string): void;
}

/**
 * Which customer (or supplier) to work as. A party the way used to sign in doesn't satisfy is shown, unavailable, with
 * the ways that work, rather than hidden (security review I1).
 */
export function ChoosePartyScreen({
  state,
  wording,
  busy = false,
  onChoose,
}: ChoosePartyScreenProps) {
  const noun = wording.partyNoun;
  return (
    <SignInFrame
      eyebrow={wording.brand}
      title={`Which ${noun}?`}
      subtitle={
        <>
          <b>{state.address}</b> can use the portal for {state.parties.length}{" "}
          {noun}s.
        </>
      }
      footer="You can switch later from the menu at the top right."
    >
      <Card className="gap-3 p-5">
        {state.parties.map((party) => {
          const unavailable = party.unavailable;
          return (
            <Button
              key={party.partyId}
              type="button"
              variant="outline"
              disabled={busy || unavailable !== null}
              onClick={() => onChoose(party.partyId)}
              className={cn(
                "h-auto min-h-10 w-full justify-start gap-2.5 px-3.5 py-2 text-left font-normal whitespace-normal",
                unavailable &&
                  "cursor-not-allowed disabled:pointer-events-auto disabled:opacity-65",
              )}
            >
              <span
                aria-hidden
                style={
                  party.brandColour
                    ? { backgroundColor: party.brandColour }
                    : undefined
                }
                className="flex h-6 shrink-0 items-center rounded-md bg-primary px-2.5 text-[10px] font-extrabold tracking-[0.06em] text-primary-foreground"
              >
                {party.initials}
              </span>
              <span className="flex min-w-0 flex-col items-start gap-0.5">
                <span>{party.name}</span>
                {unavailable ? (
                  <span className="text-[13px] font-normal text-muted-foreground">
                    Not available with the way you signed in. Use{" "}
                    {unavailable.waysThatWork}.
                  </span>
                ) : null}
              </span>
              {party.accessNote ? (
                <span className="ml-auto shrink-0 rounded-full bg-muted px-[7px] py-px text-[11px] font-semibold text-muted-foreground">
                  {party.accessNote}
                </span>
              ) : null}
            </Button>
          );
        })}
      </Card>
    </SignInFrame>
  );
}
